/* Proposal panel — modal over the white Architect editor. Builds a
   client proposal from the architecture poster and the APPROVED estimate,
   lists a project's proposals, and drives the role-aware actions:

     • Generate (generator/estimator/root) — a tracked async RUN that
       shows the same «крутая плашка» stepper as the estimate flow,
       resuming via /runs/active on open.
     • Approve (generator/root, status 'review') — behind a confirm; after
       approval the estimator no longer sees the proposal and the CEO
       (root) sets prices.
     • Pricing editor (root only) — an expandable, aligned set of price
       rows that PUTs to the server, which re-renders the .docx.

   Everything is English (the editor surface), white-label throughout, and
   mobile-friendly (the pricing grid stacks ≤900px). Scoped .arch-prop-*
   styles live in architect.css. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  proposalApi,
  ProposalAccess,
  ProposalMeta,
  ProposalRun,
  ProposalPricing,
  ProposalSpec,
} from './proposalApi';
import { ApprovalProgress, approvalErrorText, architectApi, Pipeline } from './api';
import { useAuth } from '../auth/AuthContext';
import {
  ApprovalNote,
  canApproveNow,
  myApprovalTime,
  useApprovalStatuses,
} from './ProjectsView';
import { fmtDate, modelDisplayEstimate, mapKnownRunMessage } from './utils';
import { RunStepper, stageStep, isOperatorStop } from './RunProgress';
import { toast } from './toast';
import ProposalDocEditor from './ProposalClientEditor';
import { RefreshCw } from 'lucide-react';
import { StageAiButton, StageFooter, StageHeader } from './StageChrome';
import './proposal-workspace.css';

// Wire values are the API contract; labels are what the architect sees
// (claude-opus-4-8 → "Lite", gpt-5.4 → "Max"). Same two models as the
// estimate flow. Exported so ProposalChat's model picker shares the list.
export const PROPOSAL_MODELS = [
  { value: 'claude-opus-4-8', label: 'Lite' },
  { value: 'gpt-5.4', label: 'Max' },
];

// Poll cadence for an in-flight proposal run (ms) — matches the poster.
const RUN_POLL_MS = 1500;

// The proposal stepper's five steps + per-step descriptions. Same shape
// as the estimate stepper, worded for the proposal builder.
const PROP_STEPS = [
  'Prepare sources',
  'AI is thinking',
  'Validate',
  'Render document',
  'Save',
];
const PROP_STEP_DESC = [
  'Gathering the architecture and the approved estimate',
  'The AI writes the proposal following the template',
  'Checking the proposal against the schema',
  'Turning the validated proposal into the document',
  'Storing the proposal',
];

/** Human message for a failed proposal run — the backend stores a
    white-label message in run.error. Normalise the known sweep/stop text
    (English or a legacy-Russian message from an old pod mid-deploy) to
    canonical English before falling back. */
function proposalRunErrorText(raw: string | null): string {
  return mapKnownRunMessage(raw) || 'The proposal could not be completed — try again';
}

/* DESIGN23 — «Approved — CEO pricing» described the OLD order, where the
   prices came after the approval. They now come before it, so an approved
   proposal is simply approved: its numbers are already final. */
const STATUS_LABEL: Record<ProposalMeta['status'], string> = {
  review: 'Awaiting architect approval',
  approved: 'Approved',
};

/** DESIGN12 §F4 — the muted second line of the proposal empty state: name the
    upstream approval that's still missing so the empty state is never a
    dead-end. Falls back to a neutral prompt when the pipeline isn't loaded. */
function proposalGateHint(pipeline?: Pipeline | null): string {
  if (!pipeline) {
    return 'The proposal starts automatically after pricing approval.';
  }
  const archOk = pipeline.architecture === 'approved';
  const estOk = pipeline.estimate === 'approved';
  if (!archOk && !estOk) {
    return 'Approve architecture, estimate and pricing in order.';
  }
  if (!archOk) {
    return 'Approve the architecture to continue the review sequence.';
  }
  if (!estOk) {
    return 'Approve the estimate, then pricing will unlock.';
  }
  return 'The proposal starts automatically after pricing approval.';
}

/* DESIGN23 — the PRICING GATE. The proposal is approved with its FINAL
   numbers, so the server refuses an approve until root has set real prices
   (400 pricing_required). The button is therefore disabled until then, with a
   hint that names the next step — and who has to take it, since only the CEO
   can enter prices. It applies to NEW approvals only: an already-approved
   proposal keeps its status and never shows this. */
const PRICING_GATE_ROOT =
  'Approve pricing in the Pricing stage first.';
const PRICING_GATE_OTHER =
  'Set the prices first — the CEO sets them, then this can be approved.';

interface Props {
  open: boolean;
  presentation?: 'modal' | 'workspace';
  active?: boolean;
  revision?: number;
  onChanged?: () => void;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
  projectId: string;
  projectName: string;
  access: ProposalAccess;
  /** DESIGN12 §F4 — the project's pipeline snapshot, used to explain the empty
      state (which upstream approval is still missing). Optional so the panel
      still renders if a caller hasn't loaded it yet. */
  pipeline?: Pipeline | null;
  onClose: () => void;
}

export default function ProposalPanel({
  open,
  projectId,
  projectName,
  access,
  pipeline,
  onClose,
  presentation = 'modal',
  active = true,
  revision = 0,
  onChanged,
  onDraftStateChange,
}: Props) {
  const workspace = presentation === 'workspace';
  const { user } = useAuth();
  const [proposals, setProposals] = useState<ProposalMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [listLoadedFor, setListLoadedFor] = useState<string | null>(null);
  const [runCheckedFor, setRunCheckedFor] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<ProposalRun | null>(null);
  const [runStartMs, setRunStartMs] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [model, setModel] = useState(PROPOSAL_MODELS[0].value);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  // The proposal whose document is open in the full-screen editor (the modal
  // steps aside while it's up, and reopens when the editor closes).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [legacyPricing, setLegacyPricing] = useState(false);
  const editingIdRef = useRef<string | null>(null);
  editingIdRef.current = editingId;
  const documentDraftDirty = useRef(false);
  const latestKnownId = useRef<string | null>(null);
  // DESIGN23 §C3 — bumped after an approve so the «Approved 1 of 2» counters
  // refetch even when the row set itself hasn't moved (a recorded vote below
  // the threshold leaves the proposal exactly where it was).
  const [apprRev, setApprRev] = useState(0);

  // Pricing editor (root only): which row is expanded, the per-proposal
  // spec + editable pricing draft, and the in-flight save id.
  const [pricingId, setPricingId] = useState<string | null>(null);
  const [pricingSpec, setPricingSpec] = useState<ProposalSpec | null>(null);
  const [pricingDraft, setPricingDraft] = useState<ProposalPricing | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  const firstWorkspaceLoad = useRef<string | null>(null);
  const pollRef = useRef<number | null>(null);
  // The run id the poll loop is committed to (null when idle) — lets a tick
  // drop a stale response after a Stop clears the run or a new one supersedes
  // it, so it can't clobber the live run (DESIGN13 §F3).
  const pollRunIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!access.canSee) return;
    setLoading(true);
    try {
      const r = await proposalApi.listProposals(projectId);
      const items = r.proposals || [];
      setListLoadedFor(projectId);
      setProposals(items);
      const newestId = items[0]?.id || null;
      const isNewVersion = newestId !== latestKnownId.current;
      latestKnownId.current = newestId;
      // Only the initial visit opens the latest document. Refreshing metadata
      // must never interrupt a draft or reopen a document from version history.
      if (workspace && firstWorkspaceLoad.current !== projectId) {
        firstWorkspaceLoad.current = projectId;
        if (access.canSee && newestId) setEditingId(newestId);
      } else if (workspace && access.canSee && isNewVersion && newestId) {
        if (documentDraftDirty.current) toast('A newer proposal is ready in Versions. Save your draft before switching.', 'info');
        else setEditingId(newestId);
      }
    } catch (err: any) {
      toast(err.message, 'err');
    } finally {
      setLoading(false);
    }
  }, [projectId, workspace, access.canSee]);

  // The open document keeps its actual threshold after final approval.
  const reviewIds = useMemo(
    () =>
      access.canSee
        ? proposals.filter((p) => p.status === 'review' || p.id === editingId).map((p) => p.id)
        : [],
    [access.canSee, proposals, editingId],
  );
  const approvals = useApprovalStatuses(
    'proposal',
    'proposal',
    reviewIds,
    access.canSee,
    apprRev,
  );

  // Historical proposals without a project pricing sheet keep their old
  // per-document pricing editor. New projects use the dedicated Pricing stage.
  useEffect(() => {
    if (!open || !access.isRoot || !proposals.length) return;
    let cancelled = false;
    void architectApi.getPricing(projectId).then(result => {
      if (!cancelled) setLegacyPricing(result.legacy_pricing === true);
    }).catch(() => { if (!cancelled) setLegacyPricing(false); });
    return () => { cancelled = true; };
  }, [open, access.isRoot, projectId, proposals.length]);

  /* ── load the list whenever the modal opens ──────────── */
  useEffect(() => {
    if (open && (!workspace || active)) load();
  }, [open, load, workspace, active, revision]);

  /* ── run lifecycle (async generate) — mirrors EstimatePanel ── */
  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const clearRun = useCallback(() => {
    stopPolling();
    pollRunIdRef.current = null;
    setRunning(false);
    setActiveRun(null);
    setRunStartMs(null);
  }, [stopPolling]);

  const beginPolling = useCallback(
    (runId: string) => {
      stopPolling();
      pollRunIdRef.current = runId;
      // Reentrancy guard: a GET slower than the poll interval could put
      // two ticks in flight; without this both could settle twice.
      let inFlight = false;
      let settled = false;
      const tick = async () => {
        if (inFlight || settled) return;
        inFlight = true;
        let run: ProposalRun;
        try {
          run = (await proposalApi.getProposalRun(runId)).run;
        } catch (e: any) {
          inFlight = false;
          if (e.status === 404) {
            settled = true;
            clearRun();
            toast('The proposal run was lost — try again', 'err');
          }
          return;
        }
        inFlight = false;
        // Stopped or superseded while this GET was in flight — drop the stale
        // response so it can't re-show or clobber a newer run.
        if (settled || pollRunIdRef.current !== runId) return;
        setActiveRun(run);
        if (run.status === 'done') {
          settled = true;
          clearRun();
          await load();
          onChanged?.();
          if (run.proposal_id) {
            const id = run.proposal_id;
            if (workspace && access.canSee) setEditingId((current) => current || id);
            setFlashId(id);
            window.setTimeout(
              () => setFlashId((cur) => (cur === id ? null : cur)),
              1400,
            );
          }
          toast('Proposal ready — review it below', 'ok');
        } else if (run.status === 'error') {
          settled = true;
          clearRun();
          // An operator stop is not a failure (reaches here when stopped in
          // another tab; §F4) — clear the inline error and toast neutrally.
          if (isOperatorStop(run.error)) {
            setRunError(null);
            toast('Generation stopped');
          } else {
            const msg = proposalRunErrorText(run.error);
            setRunError(msg);
            toast(msg, 'err');
          }
        }
      };
      tick();
      pollRef.current = window.setInterval(tick, RUN_POLL_MS);
    },
    [stopPolling, clearRun, load, onChanged, workspace, access.canSee],
  );

  /* ── RESUME any still-running run on open ─────────────── */
  useEffect(() => {
    if (!open || !access.canSee || (workspace && !active)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { run } = await proposalApi.getActiveProposalRun(projectId);
        if (cancelled) return;
        setRunCheckedFor(projectId);
        if (!run || run.status !== 'running') return;
        setActiveRun(run);
        setRunStartMs(Date.parse(run.created_at) || Date.now());
        setRunning(true);
        setRunError(null);
        beginPolling(run.id);
      } catch (_) {
        /* nothing to resume */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, beginPolling, workspace, active, revision, access.canSee]);

  /* ── stop polling if the panel unmounts ──────────────── */
  useEffect(() => () => stopPolling(), [stopPolling]);

  /* ── elapsed ticker driven by the run's wall-clock start ── */
  useEffect(() => {
    if (runStartMs == null) {
      setElapsedMs(0);
      return undefined;
    }
    const upd = () => setElapsedMs(Math.max(0, Date.now() - runStartMs));
    upd();
    const t = window.setInterval(upd, 1000);
    return () => window.clearInterval(t);
  }, [runStartMs]);

  /* ── close on Escape (never while a run is in flight or the document
        editor is open — the editor owns Escape then) ── */
  useEffect(() => {
    if (!open || !active || workspace) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !running && !editingId) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, active, workspace, running, editingId, onClose]);

  /* ── drop any in-flight editor when the panel itself closes ── */
  useEffect(() => {
    if (!open) setEditingId(null);
  }, [open]);

  const closeEditor = useCallback(
    (changed: boolean) => {
      setEditingId(null);
      if (changed) {
        // The editor may have restructured the fee blocks — a still-open
        // pricing panel would render (and save!) against the OLD structure,
        // so drop it; root reopens and sees the fresh state. (Inlined rather
        // than closePricing(): that callback is declared further down.)
        setPricingId(null);
        setPricingSpec(null);
        setPricingDraft(null);
        load(); // status/pricing may have moved — refresh the list
      }
    },
    [load],
  );

  const requestClose = useCallback(() => {
    if (running) return; // don't abandon a live run
    onClose();
  }, [running, onClose]);

  const generate = useCallback(async () => {
    if (!access.canGenerate || running) return;
    setRunning(true);
    setRunError(null);
    try {
      const { run } = await proposalApi.generateProposal(projectId, model);
      setActiveRun(run);
      setRunStartMs(Date.parse(run.created_at) || Date.now());
      beginPolling(run.id);
    } catch (err: any) {
      toast(err.message, 'err');
      setRunning(false);
    }
  }, [access.canGenerate, running, projectId, model, beginPolling]);

  /** Stop the active proposal run (DESIGN13 §F3). Confirmed inside the
      stepper's button; cancel is idempotent server-side and returns the
      updated run. A stopped run clears the stepper immediately with a neutral
      toast; a run that raced to a natural finish is handed to the poll loop. */
  const stopRun = useCallback(async () => {
    const run = activeRun;
    if (!run) return;
    try {
      const { run: stopped } = await proposalApi.cancelProposalRun(run.id);
      if (isOperatorStop(stopped.error)) {
        clearRun();
        setRunError(null);
        toast('Generation stopped');
      } else {
        setActiveRun(stopped);
      }
    } catch (err: any) {
      toast(err.message, 'err');
    }
  }, [activeRun, clearRun]);

  const download = useCallback(async (prop: ProposalMeta) => {
    setDownloadingId(prop.id);
    try {
      const res = await proposalApi.proposalDocxBlob(prop.id);
      const blob = await res.blob();
      let filename = 'Proposal — ' + (prop.client || 'Client') + '.docx';
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      if (m) filename = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast('Document downloaded', 'ok');
    } catch (err: any) {
      toast(err.message, 'err');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  /* DESIGN23 §C3 — approve now has TWO new shapes on top of today's:
       • the server may record a vote WITHOUT flipping the status (a
         configured multi-approver policy) — the response's `approvals` block
         says how far it got, and nothing else on the row may change;
       • it may refuse with 400 pricing_required / 403 not_an_approver. Those
         codes live in the approvals table, not in the proposal client's own
         one, so the message is post-mapped to the same English copy.
     With no policy and priced numbers the flow is byte-for-byte today's. */
  const approve = useCallback(
    async (prop: ProposalMeta) => {
      if (approvingId) return;
      setApprovingId(prop.id);
      try {
        // proposalApi predates the approvals block; read it tolerantly.
        let document: { revision: number; etag?: string } | undefined;
        if (prop.client_document_edited || prop.client_document_locked) {
          const full = (await proposalApi.getProposal(prop.id)).proposal;
          const response = await proposalApi.proposalDocxBlob(prop.id);
          document = { revision: full.document_revision || 0, etag: response.headers.get('etag') || undefined };
        }
        const r: any = document ? await proposalApi.approveProposal(prop.id, document) : await proposalApi.approveProposal(prop.id);
        const progress = r?.approvals as ApprovalProgress | undefined;
        const met = !progress || progress.given >= progress.required;
        toast(
          met
            ? 'Proposal approved'
            : 'Your approval is recorded — ' +
                progress!.given +
                ' of ' +
                progress!.required,
          'ok',
        );
        await load();
        onChanged?.();
      } catch (err: any) {
        toast(approvalErrorText(err), 'err');
      } finally {
        setApprovingId(null);
        setApprRev((n) => n + 1);
      }
    },
    [approvingId, load, onChanged],
  );

  /* ── pricing editor (root) ───────────────────────────── */
  const closePricing = useCallback(() => {
    setPricingId(null);
    setPricingSpec(null);
    setPricingDraft(null);
  }, []);

  const openPricing = useCallback(
    async (prop: ProposalMeta) => {
      if (pricingId === prop.id) {
        closePricing();
        return;
      }
      setPricingId(prop.id);
      setPricingSpec(null);
      setPricingDraft(null);
      setPricingLoading(true);
      try {
        const { proposal } = await proposalApi.getProposal(prop.id);
        // The editor may have been switched to another proposal (or closed)
        // while this GET was in flight — a stale response must not bind its
        // spec/prices to the currently open row.
        let stale = false;
        setPricingId((cur) => {
          if (cur !== prop.id) stale = true;
          return cur;
        });
        if (stale) return;
        setPricingSpec(proposal.spec || {});
        setPricingDraft(normalisePricing(proposal.pricing, proposal.spec));
      } catch (err: any) {
        toast(err.message, 'err');
        setPricingId((cur) => (cur === prop.id ? null : cur));
      } finally {
        setPricingLoading(false);
      }
    },
    [pricingId, closePricing],
  );

  const savePricing = useCallback(
    async (prop: ProposalMeta) => {
      if (!pricingDraft || savingPricing || prop.status === 'approved') return;
      setSavingPricing(true);
      try {
        const { proposal } = await proposalApi.updatePricing(
          prop.id,
          pricingDraft,
        );
        // Adopt the server-cleaned pricing so the inputs reflect what was
        // stored, and refresh the list meta (updated_at / has_docx).
        setPricingSpec(proposal.spec || {});
        setPricingDraft(normalisePricing(proposal.pricing, proposal.spec));
        toast('Prices saved — document re-rendered', 'ok');
        await load();
        onChanged?.();
      } catch (err: any) {
        toast(err.message, 'err');
      } finally {
        setSavingPricing(false);
      }
    },
    [pricingDraft, savingPricing, load, onChanged],
  );

  // Immutable field updaters for the pricing draft.
  const setSetupPrice = useCallback((i: number, value: string) => {
    setPricingDraft((d) =>
      d
        ? { ...d, setup: d.setup.map((s) => (s.i === i ? { ...s, price: value } : s)) }
        : d,
    );
  }, []);
  const setMonthlyPrice = useCallback((i: number, value: string) => {
    setPricingDraft((d) =>
      d
        ? {
            ...d,
            monthly: d.monthly.map((s) =>
              s.i === i ? { ...s, price: value } : s,
            ),
          }
        : d,
    );
  }, []);
  const setMonthlyOverage = useCallback((i: number, value: string) => {
    setPricingDraft((d) =>
      d
        ? {
            ...d,
            monthly: d.monthly.map((s) =>
              s.i === i ? { ...s, overage: value } : s,
            ),
          }
        : d,
    );
  }, []);
  const setCaseWeeks = useCallback((idx: number, value: string) => {
    setPricingDraft((d) =>
      d
        ? {
            ...d,
            case_weeks: d.case_weeks.map((c, j) =>
              j === idx ? { ...c, weeks: value } : c,
            ),
          }
        : d,
    );
  }, []);
  const setTotalWeeks = useCallback((value: string) => {
    setPricingDraft((d) => (d ? { ...d, total_weeks: value } : d));
  }, []);

  if (!open || !access.canSee) return null;

  // The document editor takes over the whole workspace; the modal steps aside
  // until it closes. Editing is generator/root-only (only reachable from the
  // canGenerate Edit button below).
  if (editingId) {
    return (
      <ProposalDocEditor
        proposalId={editingId}
        access={access}
        presentation={presentation}
        active={active}
        revision={revision}
        onChanged={() => { setApprRev(value => value + 1); void load(); onChanged?.(); }}
        onDraftStateChange={state => { documentDraftDirty.current = state.dirty; onDraftStateChange?.(state); }}
        approvalStatus={approvals[editingId]}
        approvalUserId={user?.id}
        onClose={closeEditor}
      />
    );
  }

  const canStartProposal = workspace && access.canGenerate && pipeline?.pricing === 'approved' &&
    listLoadedFor === projectId && runCheckedFor === projectId && !loading && !running &&
    !pipeline?.running?.proposal && !proposals.length;
  const setupFees = pricingSpec?.setup_fees || [];
  const monthlyFees = pricingSpec?.monthly_fees || [];

  return (
    <div
      className={workspace ? 'arch-proposal-workspace' : 'arch-modal-overlay'}
      onMouseDown={(e) => {
        if (!workspace && e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className={workspace ? 'arch-proposal-workspace-list' : 'arch-modal'}
        role={workspace ? 'region' : 'dialog'}
        aria-modal={workspace ? undefined : true}
        aria-label={'Proposal — ' + projectName}
      >
        {workspace ? <StageHeader title="Proposal" status={proposals.length ? `${proposals.length} versions` : running ? 'Preparing' : undefined} actions={[
          { id: 'refresh', label: 'Refresh versions', icon: <RefreshCw size={17}/>, onClick: () => { void load(); }, disabled: loading },
        ]} decision={<StageFooter placement="header" stage="proposal" pending={proposals[0]?.status !== 'approved'} status={approvals[proposals[0]?.id || '']} userId={user?.id} title={proposals.length ? 'Proposal history' : running ? 'Preparing proposal' : 'Waiting for pricing'} />}/> : <div className="arch-modal-head">
          <span className="arch-modal-title">Proposal — {projectName}</span>
          {!workspace ? <button
            className="arch-btn arch-btn-ghost arch-btn-sm"
            aria-label="Close"
            disabled={running}
            onClick={requestClose}
          >
            ✕
          </button> : null}
        </div>}

        <div className={workspace ? 'arch-proposal-workspace-content' : 'arch-modal-body'}>
          {activeRun ? (
            <div className="arch-est-stepper">
              <RunStepper
                steps={PROP_STEPS}
                stepDesc={PROP_STEP_DESC}
                active={stageStep(activeRun.stage)}
                elapsedMs={elapsedMs}
                heading={'Building the proposal — ' + projectName}
                modelChip={modelDisplayEstimate(activeRun.model || model)}
                stage={activeRun.stage}
                updatedAt={activeRun.updated_at}
                onStop={access.canGenerate ? stopRun : undefined}
                caption="You can leave this page — the proposal continues on the server."
              />
            </div>
          ) : null}

          {!workspace && access.canGenerate && <div className="arch-est-launch">
            <label className="arch-inline-field">
              <span>Model</span>
              <select
                aria-label="Proposal model"
                value={model}
                disabled={running}
                onChange={(e) => setModel(e.target.value)}
              >
                {PROPOSAL_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="arch-btn arch-btn-primary"
              disabled={running}
              onClick={generate}
            >
              {running ? <span className="arch-spinner" /> : null}
              <span>{running ? 'Building…' : 'Generate proposal'}</span>
            </button>
          </div>}

          {runError ? (
            <div className="arch-est-run-error" role="alert">
              ✕ {runError}
            </div>
          ) : null}

          <div className="arch-est-list">
            {loading && !proposals.length ? (
              <div className="arch-empty">Loading proposals…</div>
            ) : !proposals.length ? (
              <div className="arch-empty arch-prop-empty">
                <div className="arch-prop-empty-lead">
                  A proposal is built from the approved architecture and the
                  approved estimate.
                </div>
                <div className="arch-prop-empty-sub">{canStartProposal ? 'Ready to prepare the client proposal.' : proposalGateHint(pipeline)}</div>
                {canStartProposal ? <button type="button" className="arch-btn arch-btn-primary" onClick={() => void generate()}>Generate proposal</button> : null}
              </div>
            ) : (
              // Only the LATEST proposal is shown (newest first from the
              // server): «предыдущие не нужны — одна последняя, вся видна».
              (workspace ? proposals : proposals.slice(0, 1)).map((prop) => {
                const isPricingOpen = pricingId === prop.id;
                // DESIGN23 §C3 — approval state for THIS row (null ⇒ no
                // policy ⇒ today's behaviour) + the pricing gate.
                const apprSt = approvals[prop.id];
                const mineAt = myApprovalTime(apprSt, user?.id);
                const pendingApproval =
                  (access.canApprove ?? access.canGenerate) && prop.status === 'review';
                const showApprove =
                  pendingApproval && (!access.approvalConfigured || !!apprSt) && canApproveNow(apprSt) && !mineAt;
                const pricingGate = showApprove && !prop.priced;
                return (
                  <div
                    className={
                      'arch-est-row arch-prop-row' +
                      (prop.id === flashId ? ' flash' : '')
                    }
                    key={prop.id}
                  >
                    <div className="arch-est-row-head">
                      <span className={'arch-est-chip ' + prop.status}>
                        {STATUS_LABEL[prop.status]}
                      </span>
                      <span className="arch-est-model">
                        {modelDisplayEstimate(prop.model)}
                      </span>
                    </div>

                    <div className="arch-est-title">
                      {prop.project || prop.client || 'Proposal'}
                    </div>

                    <div className="arch-est-meta">
                      {prop.created_by_name
                        ? 'Created by ' + prop.created_by_name
                        : 'Created'}
                      {' · ' + fmtDate(prop.created_at)}
                    </div>
                    {prop.status === 'approved' && prop.approved_by_name ? (
                      <div className="arch-est-meta">
                        Approved by {prop.approved_by_name}
                        {prop.approved_at ? ' · ' + fmtDate(prop.approved_at) : ''}
                      </div>
                    ) : null}

                    <div className="arch-est-actions">
                      {/* Approved versions stay available as read-only history. */}
                      {access.canSee ? (
                        <button
                          className="arch-btn arch-btn-outline arch-btn-sm"
                          title={prop.status === 'approved' ? 'Approved version · read only' : undefined}
                          onClick={() => setEditingId(prop.id)}
                        >
                          {workspace ? 'Open document' : prop.status === 'approved' || !access.canGenerate ? 'View' : 'Edit'}
                        </button>
                      ) : null}

                      {prop.has_docx ? (
                        <button
                          className="arch-btn arch-btn-outline arch-btn-sm"
                          disabled={downloadingId === prop.id}
                          onClick={() => download(prop)}
                        >
                          {downloadingId === prop.id
                            ? 'Preparing…'
                            : 'Download .docx'}
                        </button>
                      ) : null}

                      {showApprove && !(prop.client_document_edited || prop.client_document_locked) ? (
                        <button
                          className="arch-btn arch-btn-primary arch-btn-sm"
                          disabled={approvingId === prop.id || pricingGate}
                          title={
                            pricingGate
                              ? access.isRoot
                                ? PRICING_GATE_ROOT
                                : PRICING_GATE_OTHER
                              : undefined
                          }
                          onClick={() => approve(prop)}
                        >
                          {approvingId === prop.id ? 'Approving…' : 'Approve'}
                        </button>
                      ) : null}

                      {access.isRoot && legacyPricing ? (
                        <button
                          className="arch-btn arch-btn-outline arch-btn-sm"
                          aria-expanded={isPricingOpen}
                          onClick={() => openPricing(prop)}
                        >
                          {isPricingOpen ? 'Close pricing' : prop.status === 'approved' ? 'View pricing' : 'Pricing'}
                        </button>
                      ) : null}
                    </div>

                    {/* «Approved 1 of 2», the quiet «You approved · <time>»
                        state, or the muted «Waiting for the assigned
                        approvers» line. Renders NOTHING while no policy is
                        configured. */}
                    <ApprovalNote
                      status={apprSt}
                      userId={user?.id}
                      pending={pendingApproval}
                      className="arch-appr-row"
                    />

                    {/* The pricing gate, spelled out — a disabled button with
                        only a tooltip would be a dead end. */}
                    {pricingGate ? (
                      <div className="arch-prop-price-gate">
                        {access.isRoot ? PRICING_GATE_ROOT : PRICING_GATE_OTHER}
                      </div>
                    ) : null}

                    {/* Selected reviewers receive the exact priced client file. */}
                    {!access.isRoot && prop.priced && !prop.can_view_client_document ? (
                      <div className="prop-ceo-note">
                        <span className="prop-ceo-note-tag">Pricing — CEO</span>
                        Pricing is set — visible to the CEO only.
                      </div>
                    ) : null}

                    {access.isRoot && isPricingOpen ? (
                      <div className="arch-prop-pricing">
                        {pricingLoading || !pricingDraft ? (
                          <div className="arch-empty">Loading pricing…</div>
                        ) : (
                          <>
                            {setupFees.length ? (
                              <div className="arch-prop-fee-group">
                                <div className="arch-prop-fee-title">
                                  One-time setup
                                </div>
                                {setupFees.map((fee, i) => {
                                  const row = pricingDraft.setup.find(
                                    (s) => s.i === i,
                                  );
                                  return (
                                    <div className="arch-prop-price-row" key={i}>
                                      <label className="arch-prop-price-label">
                                        {fee.case || 'Setup ' + (i + 1)}
                                      </label>
                                      <input
                                        className="arch-prop-price-input"
                                        type="text"
                                        readOnly={prop.status === 'approved'}
                                        inputMode="text"
                                        placeholder="$XX,XXX.00"
                                        value={row?.price || ''}
                                        onChange={(e) =>
                                          setSetupPrice(i, e.target.value)
                                        }
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}

                            {monthlyFees.length ? (
                              <div className="arch-prop-fee-group">
                                <div className="arch-prop-fee-title">
                                  Monthly
                                </div>
                                {monthlyFees.map((fee, i) => {
                                  const row = pricingDraft.monthly.find(
                                    (s) => s.i === i,
                                  );
                                  return (
                                    <div
                                      className="arch-prop-price-row arch-prop-price-row-monthly"
                                      key={i}
                                    >
                                      <label className="arch-prop-price-label">
                                        {fee.label || 'Monthly ' + (i + 1)}
                                      </label>
                                      <input
                                        className="arch-prop-price-input"
                                        type="text"
                                        readOnly={prop.status === 'approved'}
                                        placeholder="$XXX.00"
                                        value={row?.price || ''}
                                        onChange={(e) =>
                                          setMonthlyPrice(i, e.target.value)
                                        }
                                      />
                                      <input
                                        className="arch-prop-price-input arch-prop-overage"
                                        type="text"
                                        readOnly={prop.status === 'approved'}
                                        placeholder="+$1 per additional call (optional)"
                                        value={row?.overage || ''}
                                        onChange={(e) =>
                                          setMonthlyOverage(i, e.target.value)
                                        }
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}

                            <div className="arch-prop-fee-group">
                              <div className="arch-prop-fee-title">Timeline</div>
                              {pricingDraft.case_weeks.map((cw, j) => (
                                <div className="arch-prop-price-row" key={j}>
                                  <label className="arch-prop-price-label">
                                    {cw.case || 'Case ' + (j + 1)}
                                  </label>
                                  <input
                                    className="arch-prop-price-input arch-prop-weeks"
                                    type="text"
                                    readOnly={prop.status === 'approved'}
                                    inputMode="numeric"
                                    placeholder="Weeks"
                                    value={cw.weeks || ''}
                                    onChange={(e) =>
                                      setCaseWeeks(j, e.target.value)
                                    }
                                  />
                                </div>
                              ))}
                              <div className="arch-prop-price-row">
                                <label className="arch-prop-price-label">
                                  Total timeline (weeks)
                                </label>
                                <input
                                  className="arch-prop-price-input arch-prop-weeks"
                                  type="text"
                                  readOnly={prop.status === 'approved'}
                                  inputMode="numeric"
                                  placeholder="Weeks"
                                  value={pricingDraft.total_weeks || ''}
                                  onChange={(e) => setTotalWeeks(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="arch-prop-pricing-actions">
                              {prop.status !== 'approved' ? <button
                                className="arch-btn arch-btn-primary arch-btn-sm"
                                disabled={savingPricing}
                                onClick={() => savePricing(prop)}
                              >
                                {savingPricing ? 'Saving…' : 'Save prices'}
                              </button> : <span className="arch-est-meta">Approved pricing · read only</span>}
                              <button
                                className="arch-btn arch-btn-ghost arch-btn-sm"
                                disabled={savingPricing}
                                onClick={closePricing}
                              >
                                Close
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
        {workspace ? <><StageAiButton disabled={!proposals.length} onClick={() => { if (proposals[0]) setEditingId(proposals[0].id); }}/></> : null}
      </div>
    </div>
  );
}

/** Build an editable pricing draft aligned to the spec's fee blocks. The
    server always prefills pricing at generation, but this defends against
    a null/partial pricing (or a spec whose block count drifted): one entry
    per setup_fees / monthly_fees block, keyed by index. */
function normalisePricing(
  pricing: ProposalPricing | null | undefined,
  spec: ProposalSpec | null | undefined,
): ProposalPricing {
  const setupFees = spec?.setup_fees || [];
  const monthlyFees = spec?.monthly_fees || [];
  const src = pricing || { setup: [], monthly: [], case_weeks: [], total_weeks: null };

  const setup = setupFees.map((_, i) => {
    const found = (src.setup || []).find((s) => s.i === i);
    return { i, price: found?.price ?? null };
  });
  const monthly = monthlyFees.map((_, i) => {
    const found = (src.monthly || []).find((s) => s.i === i);
    return { i, price: found?.price ?? null, overage: found?.overage ?? null };
  });
  const case_weeks = (src.case_weeks || []).map((cw) => ({
    case: cw.case || '',
    weeks: cw.weeks || '',
  }));

  return { setup, monthly, case_weeks, total_weeks: src.total_weeks ?? null };
}
